import React, { useMemo, useState } from 'react';
import ChannelRow from './scheduler/ChannelRow';
import { useDays } from './scheduler/dateUtils';
import { format, parseISO } from 'date-fns';

export function SchedulerGrid({
  channels = [],
  programs = [],
  dates,
  timelineHeader = [],
  filterDates,
  timelineWidth = 1000,
  onProgramEdit,
  onProgramDrop,
  sidebarWidth = 120,
  showHeaderLabel = false,
  onNavigateTimeline,
  onHeaderClick,
  viewMode = 'month', // 'day', 'month', 'year'
}) {
  const [activeDragPreview, setActiveDragPreview] = useState(null);
  
  // Determine date range based on whether filterDates is provided (search) or use timeline header
  let actualStart, actualEnd;
  let headerData;
  
  // Always determine actualStart and actualEnd first (for useDays hook)
  if (filterDates?.start && filterDates?.end) {
    // Using search filters - use the provided filter dates
    actualStart = filterDates.start;
    actualEnd = filterDates.end;
  } else if (timelineHeader.length > 0) {
    // Using selectors - derive from timeline header
    const firstHeader = timelineHeader[0];
    const lastHeader = timelineHeader[timelineHeader.length - 1];
      actualStart = firstHeader.iso;
      actualEnd = lastHeader.endIso || lastHeader.iso;
  } else {
    // Fallback
    actualStart = dates?.start || programs[0]?.since || new Date().toISOString();
    actualEnd = dates?.end || programs[0]?.till || new Date(Date.now() + 24 * 3600 * 1000).toISOString();
  }
  
  // Always call useDays hook (React hooks must be called unconditionally)
  const rawDays = useDays(actualStart, actualEnd);
  
  // Determine headerData
  headerData = timelineHeader.length > 0 ? timelineHeader : rawDays;
  
  // Transform days array for year/month views to match headerData structure
  // For multi-year and month views: create one entry per column (year or month)
  // For other views: use raw days
  const days = (timelineHeader.length > 0 && 
                ((timelineHeader[0]?.viewMode === 'year-multi') || 
                 (timelineHeader[0]?.viewMode === 'month' && viewMode === 'year')))
    ? timelineHeader.map(h => {
        if (h.viewMode === 'year-multi') {
          return {
            iso: h.iso || `${h.year}-01-01`,
            date: h.iso || `${h.year}-01-01`,
            label: h.label || String(h.year)
          };
        } else {
          // Month view
          return {
            iso: h.iso || `${h.year}-${String(h.monthIndex + 1).padStart(2, '0')}-01`,
            date: h.iso || `${h.year}-${String(h.monthIndex + 1).padStart(2, '0')}-01`,
            label: h.label || h.iso
          };
        }
      })
    : rawDays;
  
  // Calculate total width needed for all columns
  const totalDaysWidth = headerData.length * Math.max(40, timelineWidth / Math.max(1, headerData.length));
  const needsHorizontalScroll = totalDaysWidth > timelineWidth;
  
  // For year view with months and multi-year view: use headerData length for both
  // For other views: use actual days count
  const isYearViewWithMonths = viewMode === 'year' && headerData[0]?.viewMode === 'month';
  const isMultiYearView = headerData[0]?.viewMode === 'year-multi';
  const actualDaysCount = (isYearViewWithMonths || isMultiYearView) ? headerData.length : days.length;
  const headerCellWidth = Math.max(40, timelineWidth / Math.max(1, headerData.length));
  const programCellWidth = Math.max(40, timelineWidth / Math.max(1, actualDaysCount));

  const programsByChannel = useMemo(() => {
    // Helper function to normalize keys (handle numeric strings like "4" vs "04")
    const normalizeKey = (val) => {
      if (val === null || val === undefined) return null;
      const str = String(val).trim();
      if (str === '' || str === 'undefined') return null;
      // For numeric values, remove leading zeros for better matching
      // e.g., "04" becomes "4", but keep non-numeric strings as-is
      if (/^\d+$/.test(str)) {
        return String(parseInt(str, 10)); // Normalize "04" -> "4", "4" -> "4"
      }
      return str;
    };
    
    // Helper function to get all possible channel identifiers (for matching)
    const getChannelKeys = (c, index) => {
      const keys = new Set();
      const addKey = (val) => {
        const normalized = normalizeKey(val);
        if (normalized) keys.add(normalized);
      };
      addKey(c?.uuid);
      addKey(c?.id);
      addKey(c?.assetNumber);
      return Array.from(keys);
    };
    
    // Helper function to get all possible program channel identifiers
    const getProgramKeys = (p) => {
      const keys = new Set();
      const addKey = (val) => {
        const normalized = normalizeKey(val);
        if (normalized) keys.add(normalized);
      };
      addKey(p?.channelUuid);
      addKey(p?.channelId);
      addKey(p?.assetNumber);
      addKey(p?.machineId);
      return Array.from(keys);
    };
    
    // keyOwner: track which channel index owns a particular identifier
    const keyOwner = new Map();
    const channelProgramMap = new Map();

    // Initialize channels and assign ownership of identifiers to the first channel that declares them
    channels.forEach((c, index) => {
      channelProgramMap.set(index, []);
      const channelKeys = getChannelKeys(c, index);
      channelKeys.forEach(key => {
        if (key && !keyOwner.has(key)) {
          keyOwner.set(key, index);
        }
      });
    });
    
    // Deduplicate programs by their identifiers to avoid rendering the same program on multiple rows
    const uniqueProgramsMap = new Map();
    programs.forEach(p => {
      const key =
        p?.id ||
        p?.originalId ||
        p?.workTicketNumber ||
        p?.workTicketNo ||
        p?.workTicket ||
        p?.processIdExt;
      if (key) {
        if (!uniqueProgramsMap.has(key)) {
          uniqueProgramsMap.set(key, p);
        }
      } else if (!uniqueProgramsMap.has(p)) {
        uniqueProgramsMap.set(p, p);
      }
    });
    const uniquePrograms = Array.from(uniqueProgramsMap.values());

    // Assign programs to channels by matching any identifier
    uniquePrograms.forEach(p => {
      const programKeys = getProgramKeys(p);
      let assigned = false;
      
      for (const progKey of programKeys) {
        if (!progKey) continue;
        const ownerIndex = keyOwner.get(progKey);
        if (ownerIndex !== undefined) {
          const arr = channelProgramMap.get(ownerIndex);
          if (arr) {
            arr.push(p);
            assigned = true;
            break; // Match found, stop processing other keys
          }
        }
      }
      
      // Debug logging for specific program (id: 1444)
      if ((p.id === '1444' || p.id === 1444)) {
        console.log('[Channel Matching Debug] Program 1444:', {
          programId: p.id,
          programTitle: p.title,
          programKeys: programKeys,
          assigned: assigned,
          assignedToKeys: assigned ? programKeys.filter(k => keyOwner.has(k)).slice(0, 3) : [],
          availableChannelKeys: Array.from(keyOwner.keys()).slice(0, 30),
          channelCount: channels.length,
          channelsSample: channels.slice(0, 5).map((c, idx) => ({
            idx,
            uuid: c?.uuid,
            id: c?.id,
            assetNumber: c?.assetNumber,
            title: c?.title
          }))
        });
      }
      
      // If no match found, log for debugging (but don't break functionality)
      if (!assigned && programKeys.length > 0) {
        console.warn('Program not matched to any channel:', {
          programId: p.id,
          programTitle: p.title,
          programKeys: programKeys,
          availableChannelKeys: Array.from(keyOwner.keys()).slice(0, 20) // Limit to first 20 for readability
        });
      }
    });
    
    return channelProgramMap;
  }, [channels, programs, filterDates, actualStart, actualEnd]);

  return (
    <div
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: 6,
        overflowY: 'auto', // allow vertical scroll
        overflowX: needsHorizontalScroll ? 'auto' : 'hidden', // enable horizontal scroll when needed
        maxHeight: 'none', // let parent container control height (fullscreen dashboard)
      }}
    >
      {/* <div style={{ display: 'flex', background: '#0b1a33', color: '#fff' }}> */}
      <div
        style={{
          display: 'flex',
          background: '#0b1a33',
          color: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 10, // keep it above program bars
        }}
      >
        <div style={{ width: sidebarWidth, padding: '10px 12px', fontWeight: 600 }}>
          {showHeaderLabel ? 'Machine' : ''}
        </div>
        <div 
          style={{ 
            width: needsHorizontalScroll ? totalDaysWidth : '100%',
            overflow: 'hidden',
            display: 'flex',
            position: 'relative',
            height: '56px'
          }}
          data-timeline-header
          className="timeline-header"
        >
          {onNavigateTimeline && (
            <>
              <button
                type="button"
                className="timeline-nav-btn timeline-nav-btn--left"
                onClick={() => onNavigateTimeline('prev')}
                aria-label="Previous timeline window"
              >
                ‹
              </button>
              <button
                type="button"
                className="timeline-nav-btn timeline-nav-btn--right"
                onClick={() => onNavigateTimeline('next')}
                aria-label="Next timeline window"
              >
                ›
              </button>
            </>
          )}

          {headerData.map((header, index) => {
            // Determine what view mode we're in
            const viewMode = header.viewMode || headerData[0]?.viewMode || 'day';
            
            // Format header text based on view mode and available data
            let displayContent = null;
            
            if (viewMode === 'year-multi') {
              // Year view: show just the year
              displayContent = (
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>
                  {header.label || header.year || ''}
                </div>
              );
            } else if (viewMode === 'month') {
              // Month view (year view with months): show month abbreviation
              let monthLabel = header.label;
              if (!monthLabel && header.iso) {
                try {
                  monthLabel = format(parseISO(header.iso), 'MMM');
                } catch (e) {
                  monthLabel = '';
                }
              }
              displayContent = (
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>
                  {monthLabel || ''}
                </div>
              );
            } else if (viewMode === 'hour') {
              // Hour view: show hour
              const hourLabel = header.label || (header.hour !== undefined ? `${header.hour.toString().padStart(2, '0')}:00` : '');
              displayContent = (
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#fff' }}>
                  {hourLabel}
                </div>
              );
            } else {
              // Day view: show day of week and day number
              let dayOfWeek = '';
              let dayNumber = '';
              
              if (header.label) {
                // Label format is "EEE dd" (e.g., "Wed 01")
                const parts = header.label.split(' ');
                if (parts.length === 2) {
                  dayOfWeek = parts[0];
                  dayNumber = parts[1];
                } else {
                  dayOfWeek = header.label;
                }
              } else if (header.dow && header.day) {
                // From rawDays
                dayOfWeek = header.dow;
                dayNumber = header.day;
              } else if (header.date) {
                // From date object
                try {
                  const date = header.date instanceof Date ? header.date : parseISO(header.date);
                  dayOfWeek = format(date, 'EEE');
                  dayNumber = format(date, 'dd');
                } catch (e) {
                  dayOfWeek = header.date.toString();
                }
              } else if (header.iso) {
                // From ISO string
                try {
                  const date = parseISO(header.iso);
                  dayOfWeek = format(date, 'EEE');
                  dayNumber = format(date, 'dd');
                } catch (e) {
                  dayOfWeek = header.iso;
                }
              }
              
              if (dayOfWeek && dayNumber) {
                // Display day of week on top, day number below (bold)
                displayContent = (
                  <>
                    <div style={{ fontSize: '11px', fontWeight: 400, marginBottom: '2px', opacity: 0.9 }}>
                      {dayOfWeek}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
                      {dayNumber}
                    </div>
                  </>
                );
              } else if (header.label) {
                displayContent = (
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#fff' }}>
                    {header.label}
                  </div>
                );
              }
            }
            
            return (
              <div
                key={header.iso || header.value || header.year || index}
                style={{
                  width: headerCellWidth,
                  minWidth: headerCellWidth,
                  padding: '8px 4px',
                  textAlign: 'center',
                  borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: onNavigateTimeline ? 'pointer' : 'default',
                  lineHeight: '1.2',
                }}
                onClick={() => {
                  if (onHeaderClick) {
                    onHeaderClick(header);
                  } else if (onNavigateTimeline) {
                    onNavigateTimeline(header);
                  }
                }}
              >
                {displayContent || (
                  <div style={{ fontSize: '12px', fontWeight: 500, color: '#fff' }}>
                    {header.value || ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ 
        width: needsHorizontalScroll ? totalDaysWidth : '100%'
      }}>
        {channels.map((ch, idx) => {
          const chPrograms = programsByChannel.get(idx) || [];
          const chKey = ch?.uuid || ch?.id || ch?.assetNumber || `channel-${idx}`;
          
          return (
            <ChannelRow
              key={chKey}
              channel={ch}
              programs={chPrograms}
              days={days}
              headerData={headerData}
              timelineWidth={timelineWidth}
              programCellWidth={programCellWidth}
              headerCellWidth={headerCellWidth}
              onProgramEdit={onProgramEdit}
              onProgramDrop={onProgramDrop}
              sidebarWidth={sidebarWidth}
              onDragPreviewChange={setActiveDragPreview}
            />
          );
        })}
      </div>
    </div>
  );
}
